import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Shield, Plus, Edit, Trash2, Users, Search, Eye, Copy } from "lucide-react";
import CreateRoleDialog from "./CreateRoleDialog";
import EditRoleDialog from "./EditRoleDialog";
import RoleDetailsModal from "./RoleDetailsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const RolesManagementTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [viewMode, setViewMode] = useState("all"); // all, system, custom

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles");
      setLoading(false);
    }
  };

  const handleViewDetails = (role) => {
    setSelectedRole(role);
    setDetailsModalOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setEditDialogOpen(true);
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system_role) {
      toast.error("Cannot delete system roles");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/roles/${role.id}`);
      toast.success("Role deleted successfully");
      fetchRoles();
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };

  const handleDuplicateRole = async (role) => {
    try {
      const response = await axios.post(`${API}/roles`, {
        name: `${role.name} (Copy)`,
        description: role.description,
        role_scope: role.role_scope,
        level: role.level,
        permissions: role.permissions,
        is_system_role: false
      });
      toast.success("Role duplicated successfully");
      fetchRoles();
    } catch (error) {
      console.error("Error duplicating role:", error);
      toast.error("Failed to duplicate role");
    }
  };

  const filteredRoles = roles.filter(role => {
    // Exclude Super Admin (platform-only role)
    if (role.name === "Super Admin" || role.role_scope === "platform") {
      return false;
    }
    
    const matchesSearch = !searchTerm || 
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesView = 
      viewMode === "all" ||
      (viewMode === "system" && role.is_system_role) ||
      (viewMode === "custom" && !role.is_system_role);
    
    return matchesSearch && matchesView;
  });

  const systemRoles = filteredRoles.filter(r => r.is_system_role);
  const customRoles = filteredRoles.filter(r => !r.is_system_role);

  const getRoleScopeLabel = (scope) => {
    const labels = {
      company: "Company",
      subsidiary: "Subsidiary",
      multi_subsidiary: "Multi-Subsidiary",
      department: "Department",
      platform: "Platform",
      custom: "Custom"
    };
    return labels[scope] || scope;
  };

  if (loading) {
    return <div className="text-center py-8">Loading roles...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Roles & Permissions ({filteredRoles.length})
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={() => setCreateDialogOpen(true)} data-create-role-button>
                <Plus className="h-4 w-4 mr-2" />
                Create Role
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* View filters */}
          <div className="flex gap-2 mb-4">
            <Button
              size="sm"
              variant={viewMode === "all" ? "default" : "outline"}
              onClick={() => setViewMode("all")}
            >
              All Roles ({filteredRoles.length})
            </Button>
            <Button
              size="sm"
              variant={viewMode === "system" ? "default" : "outline"}
              onClick={() => setViewMode("system")}
            >
              System Roles ({systemRoles.length})
            </Button>
            <Button
              size="sm"
              variant={viewMode === "custom" ? "default" : "outline"}
              onClick={() => setViewMode("custom")}
            >
              Custom Roles ({customRoles.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Roles */}
      {(viewMode === "all" || viewMode === "system") && systemRoles.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            System Roles ({systemRoles.length})
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Pre-defined roles with standard permissions (platform admin can edit)
          </p>
          <div className="grid gap-4">
            {systemRoles.map((role) => (
              <Card key={role.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-lg">{role.name}</h3>
                        <Badge variant="secondary">System Role</Badge>
                        <Badge variant="outline">{getRoleScopeLabel(role.role_scope)}</Badge>
                        {role.level && (
                          <Badge variant="outline" className="text-xs">
                            Level {role.level}
                          </Badge>
                        )}
                      </div>
                      {role.description && (
                        <p className="text-sm text-slate-600 mb-2">{role.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          {role.permissions?.length || 0} permissions
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          Assigned to users
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleViewDetails(role)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditRole(role)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDuplicateRole(role)}
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Custom Roles */}
      {(viewMode === "all" || viewMode === "custom") && (
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Custom Roles ({customRoles.length})
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Company-specific roles with custom permissions
          </p>
          {customRoles.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Shield className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600 mb-2">No custom roles created yet</p>
                <p className="text-sm text-slate-500 mb-4">
                  Create custom roles tailored to your company's needs
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Role
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {customRoles.map((role) => (
                <Card key={role.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg">{role.name}</h3>
                          <Badge variant="outline">{getRoleScopeLabel(role.role_scope)}</Badge>
                          {role.level && (
                            <Badge variant="outline" className="text-xs">
                              Level {role.level}
                            </Badge>
                          )}
                        </div>
                        {role.description && (
                          <p className="text-sm text-slate-600 mb-2">{role.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            {role.permissions?.length || 0} permissions
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            Assigned to users
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDetails(role)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditRole(role)}
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDuplicateRole(role)}
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteRole(role)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <CreateRoleDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          fetchRoles();
          setCreateDialogOpen(false);
        }}
      />

      <EditRoleDialog
        role={selectedRole}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedRole(null);
        }}
        onSuccess={() => {
          fetchRoles();
          setEditDialogOpen(false);
        }}
      />

      <RoleDetailsModal
        role={selectedRole}
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedRole(null);
        }}
        onEdit={handleEditRole}
        onDuplicate={handleDuplicateRole}
        onDelete={handleDeleteRole}
      />
    </div>
  );
};

export default RolesManagementTab;
